import React from 'react';
import { Select, SelectProps } from 'antd';

export interface SearchableSelectProps extends Omit<SelectProps, 'showSearch' | 'filterOption'> {
  /**
   * Custom search placeholder text
   */
  searchPlaceholder?: string;
  /**
   * Whether to enable case-sensitive search (default: false)
   */
  caseSensitive?: boolean;
  /**
   * Custom filter function for options
   */
  customFilterOption?: (input: string, option: any) => boolean;
  /**
   * Whether to search in both label and value (default: true for label only)
   */
  searchInValue?: boolean;
  /**
   * Enable debug logging for search operations (default: false)
   */
  debugSearch?: boolean;
}

/**
 * Enhanced Select component with built-in search functionality
 * Provides consistent search behavior across all dropdown components
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  searchPlaceholder,
  caseSensitive = false,
  customFilterOption,
  searchInValue = false,
  debugSearch = false,
  children,
  ...selectProps
}) => {
  // const { t } = useTranslationWithNamespace('common'); // Removed unused variable

  // Enhanced filter function that searches in option labels and optionally values
  const defaultFilterOption = (input: string, option: any) => {
    if (!input) return true;

    const searchText = caseSensitive ? input.trim() : input.trim().toLowerCase();

    // Skip empty search
    if (!searchText) return true;

    // Debug logging (only when enabled)
    if (debugSearch) {
      console.log('SearchableSelect Debug - Input:', input, 'SearchText:', searchText, 'Option:', option);
    }

    // Get the option label (could be in children, label, or title)
    let optionLabel = '';
    if (option?.children) {
      if (typeof option.children === 'string') {
        optionLabel = option.children;
      } else if (Array.isArray(option.children)) {
        // Handle array children by joining text content
        optionLabel = option.children
          .map((child: any): string => {
            if (typeof child === 'string') return child;
            if (typeof child === 'number') return String(child);
            if (child && typeof child === 'object' && child.props && child.props.children) {
              return typeof child.props.children === 'string' ? child.props.children : '';
            }
            return '';
          })
          .filter((text: string) => text.length > 0)
          .join(' ');
      }
    } else if (option?.label) {
      optionLabel = typeof option.label === 'string' ? option.label : '';
    } else if (option?.title) {
      optionLabel = typeof option.title === 'string' ? option.title : '';
    }

    if (debugSearch) {
      console.log('SearchableSelect Debug - Extracted optionLabel:', optionLabel);
    }

    if (!caseSensitive) {
      optionLabel = optionLabel.toLowerCase();
    }

    // Enhanced search logic for compound labels like "BTC - Bitcoin" or "XRP - Ripple"
    let matches = false;

    // 1. Direct substring match in the full label
    matches = optionLabel.includes(searchText);
    if (debugSearch && matches) {
      console.log('SearchableSelect Debug - Match found via direct substring in:', optionLabel);
    }

    // 2. If not found, try splitting by common separators and search in parts
    if (!matches) {
      const separators = [' - ', ' – ', ' — ', ' | ', ' / ', ': '];
      for (const separator of separators) {
        if (optionLabel.includes(separator)) {
          const parts = optionLabel.split(separator);
          matches = parts.some(part => part.trim().includes(searchText));
          if (matches) {
            if (debugSearch) {
              console.log('SearchableSelect Debug - Match found via separator split in parts:', parts);
            }
            break;
          }
        }
      }
    }

    // 3. If still not found, try word-based matching (useful for partial matches)
    if (!matches) {
      const searchWords = searchText.split(/\s+/).filter(word => word.length > 0);
      const labelWords = optionLabel.split(/[\s\-–—|/:]+/).filter(word => word.length > 0);

      // Check if any search word matches the beginning of any label word
      matches = searchWords.every(searchWord =>
        labelWords.some(labelWord => labelWord.startsWith(searchWord))
      );
      if (debugSearch && matches) {
        console.log('SearchableSelect Debug - Match found via word-based matching. Search words:', searchWords, 'Label words:', labelWords);
      }
    }

    // 4. Optionally search in value as well
    if (!matches && searchInValue && option?.value) {
      const optionValue = caseSensitive ? String(option.value) : String(option.value).toLowerCase();
      matches = optionValue.includes(searchText);
      if (debugSearch && matches) {
        console.log('SearchableSelect Debug - Match found in value:', optionValue);
      }
    }

    if (debugSearch) {
      console.log('SearchableSelect Debug - Final result for option:', optionLabel, 'matches:', matches);
    }

    return matches;
  };

  // Use custom filter if provided, otherwise use default
  const filterOption = customFilterOption || defaultFilterOption;

  return (
    <Select
      {...selectProps}
      showSearch
      placeholder={selectProps.placeholder}
      filterOption={filterOption}
      optionFilterProp="children"
    >
      {children}
    </Select>
  );
};

export default SearchableSelect;
